import { AxiosResponse } from 'axios'
import {
    all,
    call,
    put,
    select,
    takeLatest,
    AllEffect,
    ForkEffect,
} from 'redux-saga/effects'
import { sagaErrorHandle } from 'utils/errHandle'
import { IPagination } from 'config/types'
import { newsTypes } from './actionTypes'

import {
    createNews,
    deleteNews,
    editNews,
    getNews,
    getNewsById,
} from '../service/news'
// eslint-disable-next-line max-len
import { handlePlanLimitsPopupError } from '../../plan-limits-popup/store/actions'

import {
    INews,
    FetchNewsRequest,
    FetchNewsByIdRequest,
    CreateNewsRequest,
    EditNewsRequest,
} from './types'

function* fetchNewsSaga(action: FetchNewsRequest) {
    try {
        const response: AxiosResponse<IPagination<INews>> = yield call(
            getNews,
            action.payload,
        )
        if (response.data) {
            yield put({
                type: newsTypes.SET_NEWS,
                payload: { news: response.data.items },
            })
            action.payload.callback('success', response.data)
        }
    } catch (error) {
        sagaErrorHandle(action.payload.callback, error)
    }
}

function* fetchNewsByIdSaga(action: FetchNewsByIdRequest) {
    try {
        const response: AxiosResponse<INews> = yield call(
            getNewsById,
            action.payload,
        )

        if (response.data) {
            if (action.payload.isEdit) {
                const oppositeLang =
                    response.data.language === 'en' ? 'uk' : 'en'
                let relativeNews: INews = {
                    id: '2',
                    title: '',
                    description: '',
                    publicationDate: new Date(Date.now()),
                    images: [],
                    status: 'Hide',
                    showDatePicker: false,
                    language: oppositeLang,
                    applicationId: action.payload.appId,
                }
                const { relativeId } = response.data
                if (relativeId) {
                    const props = {
                        appId: action.payload.appId,
                        newsId: relativeId,
                    }
                    const relativeResp: AxiosResponse<INews> = yield call(
                        getNewsById,
                        props,
                    )
                    if (relativeResp.data) {
                        relativeNews = relativeResp.data
                    }
                }
                yield put({
                    type: newsTypes.SET_RELATIVE,
                    payload: { relative: relativeNews },
                })
            }

            if (action.payload.callback) {
                action.payload.callback('success')
            }
            yield put({
                type: newsTypes.SET_ONE_PIECE_OF_NEWS,
                payload: { article: response.data },
            })
        }
    } catch (error) {
        sagaErrorHandle(action.payload.callback, error)
    }
}

function* createNewsSaga(action: CreateNewsRequest) {
    try {
        const response: AxiosResponse<INews[]> = yield call(
            createNews,
            action.payload,
        )
        if (response.status === 200) {
            if (action.payload.callback) {
                action.payload.callback('success')
            }
        }
    } catch (error) {
        sagaErrorHandle(action.payload.callback, error)
        yield put(handlePlanLimitsPopupError({ message: error[0].name }))
    }
}

function* editNewsSaga(action: EditNewsRequest) {
    try {
        const response: AxiosResponse<INews> = yield call(
            editNews,
            action.payload,
        )

        if (response.status === 200) {
            if (action.payload.callback) {
                action.payload.callback('success')
            }
        }
    } catch (error) {
        sagaErrorHandle(action.payload.callback, error)
    }
}

function* deleteNewsSaga(action: EditNewsRequest) {
    try {
        const response: AxiosResponse<INews> = yield call(
            deleteNews,
            action.payload,
        )
        if (response.status === 200) {
            if (action.payload.callback) {
                action.payload.callback('success')
            }
            const { news } = yield select(state => state.news)
            const updatedNews = news.filter(
                (article: INews) => article.id !== action.payload.newsId,
            )

            yield put({
                type: newsTypes.SET_NEWS,
                payload: { news: updatedNews },
            })
        }
    } catch (error) {
        sagaErrorHandle(action.payload.callback, error)
    }
}

function* newsSaga(): Generator<AllEffect<ForkEffect<never>>, void, unknown> {
    yield all([takeLatest(newsTypes.FETCH_NEWS_REQUEST, fetchNewsSaga)])
    yield all([takeLatest(newsTypes.FETCH_NEWS_BY_ID, fetchNewsByIdSaga)])
    yield all([takeLatest(newsTypes.CREATE_NEWS_REQUEST, createNewsSaga)])
    yield all([takeLatest(newsTypes.EDIT_NEWS_REQUEST, editNewsSaga)])
    yield all([takeLatest(newsTypes.DELETE_NEWS_REQUEST, deleteNewsSaga)])
}

export default newsSaga
